import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { Oval as Loader } from 'react-loader-spinner';

import './message.css';
import MessagingExplainedModal from '../../Modals/Message/MessagingExplainedModal';
import PurchaseCreditsModal from '../../Modals/Message/PurchaseCreditsModal';
import MessageHeader from '../../../components/layout/messageHeader';
import ModernFootermenu from '../../../components/layout/Footer';
import { MatrixContext } from '../../../MatrixProvider';
import { ROLE } from '../../../shared/constants/common';
import {
  CREDITS_DISPLAY_TEXT,
  MESSAGE_TAB_SLUGS,
  MESSAGE_TAB_FROM_SLUG,
  MESSAGE_TIP_STATUS,
  MESSAGE_REPLY_DEADLINE_DAYS_EXTENDED,
} from '../../../shared/constants/message';
import {
  getUserRole,
  isUserLoggedIn,
  getUserProfilePicture,
  formatAmountWithTwoDecimalPlaces,
  isLoginWithSupportPassword,
} from '../../../shared/utils/function';
import { fetchUser, updateUser } from '../../../shared/actions/userActions';
import {
  fetchMatrixRooms,
  fetchMatrixRoomsUntilComplete,
} from '../../../shared/actions/matrixAction';
import { useMatrixRoomsHasMessageSync } from './hooks/useMatrixRoomsHasMessageSync';
import { useMatrixRooms } from '../../../shared/utils/matrix/useMatrixRooms';
import { DEFAULT_AVATAR, filterAbandonedInboxRooms } from '../../../shared/utils/matrix/matrixRoomUtils';
import { safeString, getMatrixRoomsArray, getPendingTipsForUserType } from '../../../shared/utils/chatUtil';
import MessageCard from './Chating/card/MessageCard';
import { computeRoomIsPriority, getFirstTipDeadlineInfo } from '../../../shared/utils/matrix/matrixReadUtils';
import { captureChatException } from 'shared/sentry';
import { isAbortError } from '../../../shared/utils/isAbortError.js';

// TODO: Uncomment this when we want to delete the matrix room
// import { deleteMatrixRoom } from '../../../shared/actions/matrixAction';

const UNKNOWN_USER_LABEL = 'Unknown';
const SEARCH_DEBOUNCE_MS = 250;
const BACKGROUND_REFRESH_MS = 30000;
/** Progressive list: render in chunks so large inboxes do not block the main thread. */
const LIST_RENDER_INITIAL = 40;
const LIST_RENDER_STEP = 40;
/** After this age, `/matrix/rooms` refetch bypasses Redux session cache when tab becomes visible. */
const MATRIX_ROOMS_BACKEND_MAX_STALE_MS = 5 * 60 * 1000;

const getLoadingSubtitle = (loadingState, matrixLoading) => {
  if (loadingState === 'initializing') return 'Initializing...';
  if (loadingState === 'syncing') return 'Syncing...';
  if (matrixLoading) return 'Loading room data...';
  return 'Loading...';
};

const getLoadingMessage = (loadingState, matrixLoading) => {
  if (loadingState === 'initializing') return 'Initializing conversations...';
  if (loadingState === 'syncing') return 'Syncing conversations...';
  if (matrixLoading) return 'Loading room data...';
  return 'Loading conversations...';
};

const renderFilterTabIcon = (title) => {
  if (title === 'Unread Priority') {
    return (
      <>
        <small className="msg_f_warning">
          <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
        </small>
        <b className="dot_yellow_msg" />
      </>
    );
  }
  if (title === 'Priority') {
    return <b className="dot_green_msg" />;
  }
  return null;
};

const Messaging = () => {
  const userRole = getUserRole();
  const location = useLocation();
  const history = useHistory();

  // Redux hooks
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user?.user);
  const creator = useSelector((state) => state.creator?.creator);
  const matrixRooms = useSelector((state) => state.matrix?.rooms || {});
  const matrixLoading = useSelector((state) => state.matrix?.loading || false);
  const matrixHasRoomsLoaded = useSelector((state) => state.matrix?.hasRoomsLoaded || false);
  const matrixError = useSelector((state) => state.matrix?.error || null);
  const matrixRoomsFetchedAt = useSelector((state) => state.matrix?.roomsFetchedAt || 0);
  const roomsPagination = useSelector((state) => state.matrix?.roomsPagination || {});
  const roomsLoadingMore = useSelector((state) => state.matrix?.roomsLoadingMore || false);
  const walletBalance = useSelector((state) => state.credit?.walletBalance);

  // Context
  const { client, isInitialized, matrixUserInfo } = useContext(MatrixContext);

  // TODO: Uncomment this when we want to delete the matrix room
  // useEffect(() => {
  //   (async () => {
  //     const deleteRoomId = '!cDlQTIqdLnmYEDXkpy:stgmatrix.juicyhouse.com';
  //     if (deleteRoomId) {
  //       try {
  //         await deleteMatrixRoom(deleteRoomId);
  //       } catch (_) {}
  //     }
  //   })();
  // }, []);

  // Active message tab from URL (?tab=all | unread-priority | priority | messages | archived)
  const activeTabSlug = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab && MESSAGE_TAB_FROM_SLUG[tab]) return tab;
    return 'all';
  }, [location.search]);

  const activeTabTitle = useMemo(() => MESSAGE_TAB_FROM_SLUG[activeTabSlug] || 'All', [activeTabSlug]);

  const setActiveTab = useCallback((filterTitle) => {
    const slug = MESSAGE_TAB_SLUGS[filterTitle];
    if (!slug) return;
    const next = new URLSearchParams(location.search);
    next.set('tab', slug);
    history.replace({ ...location, search: next.toString() });
  }, [location, history]);

  // Local state
  const [showMsgOverviewModal, setShowMsgOverviewModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [roomMetadataSettled, setRoomMetadataSettled] = useState(false);
  const [roomMetadataReady, setRoomMetadataReady] = useState(false);
  const [creditsDisplayText, setCreditsDisplayText] = useState(CREDITS_DISPLAY_TEXT);
  const [showPurchaseCreditsModal, setShowPurchaseCreditsModal] = useState(false);
  const [visibleListCount, setVisibleListCount] = useState(LIST_RENDER_INITIAL);

  // Refs
  const roomsFetchKeyRef = useRef(null);
  const lastRoomsRefreshAtRef = useRef(0);
  const syncInProgressRef = useRef(false);
  const syncDebounceTimerRef = useRef(null);
  const listLoadMoreSentinelRef = useRef(null);

  // Custom hooks
  const { rooms: roomsList, loading: loadingRooms, loadingState, refreshRooms } = useMatrixRooms({
    client,
    isInitialized,
    matrixUserInfo,
    options: {
      loadingTimeout: 5000,
      fallbackTimeout: 3000,
    },
  });

  const refreshMatrixRoomsIfNeeded = useCallback(async () => {
    // Do not bail on Redux `matrixLoading`: a stuck spinner needs another dispatch so the thunk can
    // stale-abort or join the active request guard. Duplicate calls inside 45s resolve via thunk.
    // Backend `/matrix/rooms` is JuicyHouse metadata (names, tips, avatars). It does not
    // depend on Matrix `/sync` PREPARED, so do not wait for `isInitialized`.
    if (document.visibilityState !== 'visible') {
      return;
    }

    const now = Date.now();
    if (now - lastRoomsRefreshAtRef.current < 1500) { // 1.5 seconds
      return;
    }

    lastRoomsRefreshAtRef.current = now;
    const bypassSessionCache =
      matrixHasRoomsLoaded &&
      matrixRoomsFetchedAt > 0 &&
      now - matrixRoomsFetchedAt > MATRIX_ROOMS_BACKEND_MAX_STALE_MS;
    await dispatch(fetchMatrixRooms({ force: bypassSessionCache }));
  }, [dispatch, matrixHasRoomsLoaded, matrixRoomsFetchedAt]);

  const safeSync = useCallback(async () => {
    if (syncInProgressRef.current) return;
    syncInProgressRef.current = true;

    try {
      // Refresh from Matrix in-memory cache first so UI updates immediately.
      refreshRooms();
      await refreshMatrixRoomsIfNeeded();
    } catch (error) {
      if (!isAbortError(error)) {
        captureChatException(error, { tags: { feature: 'messages', matrix: 'room-sync' } });
      }
    } finally {
      syncInProgressRef.current = false;
    }
  }, [refreshMatrixRoomsIfNeeded, refreshRooms]);

  const debouncedSafeSync = useCallback(() => {
    if (syncDebounceTimerRef.current) {
      clearTimeout(syncDebounceTimerRef.current);
    }
    syncDebounceTimerRef.current = setTimeout(() => {
      safeSync();
    }, 500);
  }, [safeSync]);

  useEffect(() => {
    if (walletBalance?.credits > 0) {
      setCreditsDisplayText(`Credits: ${formatAmountWithTwoDecimalPlaces(walletBalance?.credits)}`);
    } else {
      setCreditsDisplayText(CREDITS_DISPLAY_TEXT);
    }
  }, [walletBalance]);

  // Derived data
  const isCreator =
    userRole === ROLE.CREATOR_SUB || userRole === ROLE.CREATOR_CONTENT;

  const enrichedRoomsList = useMemo(() => {
    const matrixRoomsArray = getMatrixRoomsArray(matrixRooms);

    if (!roomsList?.length) {
      return [];
    }

    if (!roomMetadataReady) {
      return filterAbandonedInboxRooms(roomsList);
    }

    const matrixRoomsMap = new Map(
      matrixRoomsArray
        .filter((matrixRoom) => matrixRoom?.roomid)
        .map((matrixRoom) => [matrixRoom.roomid, matrixRoom]),
    );

    return filterAbandonedInboxRooms(
      roomsList.map((room) => {
        const roomId = room.roomId || room._id;
        if (!roomId) return room;

        const matrixRoomData = matrixRoomsMap.get(roomId);
        if (!matrixRoomData?.otherUser) {
          return room;
        }

        const isFirstMessageTip = Boolean(matrixRoomData?.otherUser?.isFirstMessageTip);
        const isCreatorReplyToTip = Boolean(matrixRoomData?.otherUser?.isCreatorReplyToTip);
        const firstTipCreatedAt = matrixRoomData?.otherUser?.firstTipCreatedAt || null;
        const firstTipIsExtended = Boolean(matrixRoomData?.otherUser?.firstTipIsExtended);

        const isPriority = computeRoomIsPriority(matrixRoomData?.otherUser);
        const tipStatus = matrixRoomData?.otherUser?.firstTipState;
        const tipId = matrixRoomData?.otherUser?.firstTipId || null;

        let daysRemaining = null;
        let daysLeftToReply = null;
        let extended = null;
        if (
          firstTipCreatedAt &&
          isFirstMessageTip &&
          !isCreatorReplyToTip &&
          tipStatus === MESSAGE_TIP_STATUS.PENDING_CREATOR_REPLY
        ) {
          const deadlineInfo = getFirstTipDeadlineInfo(firstTipCreatedAt, firstTipIsExtended);
          if (deadlineInfo) {
            daysRemaining = deadlineInfo.label;
            daysLeftToReply = deadlineInfo.daysLeft;
            if (firstTipIsExtended) {
              extended = `Extended +${MESSAGE_REPLY_DEADLINE_DAYS_EXTENDED} days`;
            }
          }
        }

        const pendingTips = getPendingTipsForUserType(
          isCreator,
          matrixRoomData?.otherUser,
          room?.lastMessage || room?.last_message || room?.message || '',
        );

        // Enrich room with matrixRooms data (subscribed used for Archived tab: false => archived)
        return {
          ...room,
          userId: matrixRoomData.otherUser?._id || room.userId || null,
          username: matrixRoomData.otherUser.username || room.username || UNKNOWN_USER_LABEL,
          profilePicture: matrixRoomData.otherUser.profile_picture || room.profilePicture || DEFAULT_AVATAR,
          subscribed: matrixRoomData.otherUser?.subscribed || false,
          total: matrixRoomData.otherUser?.total ?? 0,
          totalSixtyDays: matrixRoomData.otherUser?.totalSixtyDays ?? 0,
          isFirstMessageTip: isFirstMessageTip,
          isCreatorReplyToTip: isCreatorReplyToTip,
          isPriority: isPriority,
          daysRemaining: daysRemaining,
          daysLeftToReply,
          extended: extended,
          tipId: tipId,
          matrixEventId: matrixRoomData?.otherUser?.firstTipMatrixEventId || null,
          tipStatus: tipStatus,
          isExtended: firstTipIsExtended,
          pendingTips,
        };
      }),
    );
  }, [roomsList, matrixRooms, roomMetadataReady, isCreator]);
  /**
   * Only show rooms where a conversation has been started:
   * - Creator messaged a subscriber, OR
   * - Subscriber messaged a creator
   * Rooms with no messages (lastTimestamp === 0) are hidden.
   */
  const filteredRoomsList = useMemo(() => {
    const roomsWithConversations = enrichedRoomsList.filter(
      (room) => room?.lastTimestamp > 0,
    );

    const q = safeString(debouncedQuery).trim().toLowerCase();
    if (!q) return roomsWithConversations;

    return roomsWithConversations.filter((room) => {
      const name = safeString(room?.username).toLowerCase();
      const lastMessage = safeString(room?.lastMessage || room?.last_message || room?.message || '').toLowerCase();
      return name.includes(q) || lastMessage.includes(q);
    });
  }, [enrichedRoomsList, debouncedQuery]);

  // Get room ids missing backend user metadata (only actionable after all pages are loaded).
  const nullUserIdRoomIds = useMemo(() => {
    return filteredRoomsList
      .filter((room) => room.userId == null)
      .map((room) => room.roomId || room._id)
      .filter(Boolean);
  }, [filteredRoomsList]);

  const allBackendPagesLoaded = matrixHasRoomsLoaded && !roomsPagination?.hasMore;

  const handleHasMessagePatchSuccess = useCallback(async () => {
    await dispatch(fetchMatrixRoomsUntilComplete({ force: true }));
  }, [dispatch]);

  const { resetPatchedRoomIds } = useMatrixRoomsHasMessageSync({
    enabled: isInitialized && roomMetadataReady,
    isFetching: matrixLoading || roomsLoadingMore,
    allPagesLoaded: allBackendPagesLoaded,
    nullUserIdRoomIds,
    onPatchSuccess: handleHasMessagePatchSuccess,
  });

  const roomsByTabSlug = useMemo(() => {
    const grouped = {
      all: filteredRoomsList,
      'unread-priority': [],
      priority: [],
      messages: [],
      archived: [],
    };

    // const myMatrixUserId = matrixUserInfo?.matrix_user_id ?? null;
    filteredRoomsList.forEach((room) => {
      const isFirstMessageTip = Boolean(room?.isFirstMessageTip);
      const isCreatorReplyToTip = Boolean(room?.isCreatorReplyToTip);
      const isPriority = Boolean(room?.isPriority);
      // Archived: explicit flag or otherUser.subscribed === false (locked for subscriber; creator can still message)
      const isArchived = Boolean(
        room?.isArchived || room?.archived || room?.is_archived || room?.subscribed === false
      );

      if (isArchived) {
        grouped.archived.push(room);
        return;
      }

      // 1) (isFirstMessageTip && isCreatorReplyToTip) OR isPriority => All + Priority tab
      if (isPriority) {
        grouped.priority.push(room);
        return;
      }

      // 2) isFirstMessageTip true and isCreatorReplyToTip false => All + Unread Priority tab
      if (isFirstMessageTip && !isCreatorReplyToTip) {
        grouped['unread-priority'].push(room);
        return;
      }

      // 3) Everything else (including expired Priority that still has tip flags) => Messages
      // if (!isFirstMessageTip && !isCreatorReplyToTip && !isPriority) {
      //   grouped.messages.push(room);
      // }
      grouped.messages.push(room);
    });

    // Sort Unread Priority by time left to reply (soonest deadline first)
    grouped['unread-priority'].sort((a, b) => {
      const aDays = typeof a.daysLeftToReply === 'number' ? a.daysLeftToReply : Number.POSITIVE_INFINITY;
      const bDays = typeof b.daysLeftToReply === 'number' ? b.daysLeftToReply : Number.POSITIVE_INFINITY;

      if (aDays !== bDays) {
        return aDays - bDays;
      }

      // Tie-breaker: most recent conversation first
      return (b.lastTimestamp || 0) - (a.lastTimestamp || 0);
    });

    return grouped;
  }, [filteredRoomsList]);

  // Backend `/matrix/rooms` is JuicyHouse metadata (names, avatars, tips). It does not
  // depend on homeserver `/sync`, so start it as soon as the inbox is shown — not after
  // Matrix PREPARED. Do not chain this API off tab-resume /sync either.
  useEffect(() => {
    if (!isUserLoggedIn()) return;

    const fetchKey = matrixUserInfo?.matrix_user_id || '__logged_in__';
    const prevKey = roomsFetchKeyRef.current;
    const switchedUser =
      typeof prevKey === 'string' &&
      prevKey !== '__logged_in__' &&
      fetchKey !== '__logged_in__' &&
      prevKey !== fetchKey;

    if (prevKey === fetchKey) return;
    roomsFetchKeyRef.current = fetchKey;

    if (switchedUser) {
      setRoomMetadataSettled(false);
      setRoomMetadataReady(false);
      resetPatchedRoomIds();
      dispatch(fetchMatrixRoomsUntilComplete({ force: true }));
      return;
    }

    if (!matrixHasRoomsLoaded) {
      dispatch(fetchMatrixRoomsUntilComplete());
    }
  }, [
    dispatch,
    matrixHasRoomsLoaded,
    matrixUserInfo?.matrix_user_id,
    resetPatchedRoomIds,
  ]);

  // Refresh rooms list if window is focused; long-idle stale metadata is refreshed
  // via refreshMatrixRoomsIfNeeded (5 min), not by chaining after Matrix `/sync`.
  useEffect(() => {
    const handleWindowFocus = () => {
      debouncedSafeSync();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        debouncedSafeSync();
      }
    };

    const handlePageShow = () => {
      debouncedSafeSync();
    };

    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (syncDebounceTimerRef.current) {
        clearTimeout(syncDebounceTimerRef.current);
      }
    };
  }, [debouncedSafeSync]);

  // Refresh rooms list every 30 seconds in background
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') {
        safeSync();
      }
    }, BACKGROUND_REFRESH_MS);

    return () => clearInterval(intervalId);
  }, [safeSync]);

  useEffect(() => {
    if (matrixLoading) return;

    if (matrixHasRoomsLoaded) {
      setRoomMetadataSettled(true);
      setRoomMetadataReady(true);
      return;
    }
    if (matrixError) {
      setRoomMetadataSettled(true);
      setRoomMetadataReady(false);
    }
  }, [matrixLoading, matrixHasRoomsLoaded, matrixError]);

  useEffect(() => {
    // Fail open: if metadata API never settles, unblock the list and show chat rooms
    // with Matrix-only data so messaging does not stay stuck on a loading spinner.
    if (matrixLoading || roomMetadataSettled || matrixHasRoomsLoaded || matrixError) {
      return undefined;
    }

    const fallbackTimer = setTimeout(() => {
      setRoomMetadataSettled(true);
      setRoomMetadataReady(false);
    }, 6000);

    return () => clearTimeout(fallbackTimer);
  }, [matrixLoading, roomMetadataSettled, matrixHasRoomsLoaded, matrixError]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    if (
      user?.isShowWarningMessageModal === false &&
      !isLoginWithSupportPassword()
    ) {
      setShowMsgOverviewModal(true);
    }
  }, [user]);

  // Event handlers
  const openMessagingExplainedModal = useCallback(() => {
    setShowMsgOverviewModal(true);
  }, []);

  const handleMessagingExplainedClose = useCallback(async () => {
    setShowMsgOverviewModal(false);

    if (isLoginWithSupportPassword()) return;
    if (user?.isShowWarningMessageModal) return;

    try {
      await dispatch(updateUser({ isShowWarningMessageModal: true }));
      await dispatch(fetchUser());
    } catch (error) {
      captureChatException(error, { tags: { feature: 'messages', action: 'warning-modal-flag' } });
    }
  }, [dispatch, user]);

  const messageFilterOptions = useMemo(
    () =>
      Object.keys(MESSAGE_TAB_SLUGS).map((title) => {
        const slug = MESSAGE_TAB_SLUGS[title];
        const roomsForTab = roomsByTabSlug[slug] || [];
        return { title, count: roomsForTab.length };
      }),
    [roomsByTabSlug],
  );

  const roomsForActiveTab = useMemo(
    () => roomsByTabSlug[activeTabSlug] || filteredRoomsList,
    [roomsByTabSlug, activeTabSlug, filteredRoomsList],
  );

  const visibleRoomsForActiveTab = useMemo(
    () => roomsForActiveTab.slice(0, visibleListCount),
    [roomsForActiveTab, visibleListCount],
  );

  const hasMoreRoomsToRender = visibleListCount < roomsForActiveTab.length;
  const hasMoreBackendRooms = Boolean(roomsPagination?.hasMore);
  const hasMoreToShow = hasMoreRoomsToRender || hasMoreBackendRooms;

  useEffect(() => {
    setVisibleListCount(LIST_RENDER_INITIAL);
  }, [activeTabSlug, debouncedQuery]);

  useEffect(() => {
    const sentinel = listLoadMoreSentinelRef.current;
    if (!sentinel || !hasMoreToShow) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;

        setVisibleListCount((prev) => {
          if (prev >= roomsForActiveTab.length) return prev;
          return Math.min(prev + LIST_RENDER_STEP, roomsForActiveTab.length);
        });

        if (hasMoreBackendRooms && !roomsLoadingMore && !matrixLoading) {
          dispatch(fetchMatrixRooms({ append: true }));
        }
      },
      { rootMargin: '240px' },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    hasMoreToShow,
    hasMoreBackendRooms,
    roomsForActiveTab.length,
    roomsLoadingMore,
    matrixLoading,
    dispatch,
  ]);

  const isLoading =
    roomsList.length === 0 &&
    (
      !isInitialized ||
      loadingRooms ||
      (matrixLoading && !matrixHasRoomsLoaded && !matrixError)
    );

  const headerAvatarSrc = useMemo(() => {
    if (!isUserLoggedIn()) return DEFAULT_AVATAR;
    return getUserProfilePicture(user, creator) || DEFAULT_AVATAR;
  }, [user, creator]);

  return (
    <>
      <div className="modern-message-page">
        <MessageHeader
          showBackButton={false}
          imgSrc={headerAvatarSrc}
          title="All Messages"
          showAtPrefixOnTitle={false}
          subtitle={
            <>
              {isLoading
                ? getLoadingSubtitle(loadingState, matrixLoading)
                : `${roomsForActiveTab.length} active conversations`}
              <span className="ml-1">
                <span
                  className="icon_hover"
                  role="button"
                  tabIndex={0}
                  onClick={openMessagingExplainedModal}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') openMessagingExplainedModal();
                  }}
                  aria-label="Messaging information"
                >
                  <i className="fa-solid fa-circle-question" aria-hidden="true" />
                </span>
              </span>
              {userRole === ROLE.USER && (
                <span
                  className="credits_plus_chip"
                  role="button"
                  tabIndex={0}
                  onClick={() => setShowPurchaseCreditsModal(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setShowPurchaseCreditsModal(true);
                    }
                  }}
                  aria-label="Purchase credits"
                >
                  {creditsDisplayText}
                </span>
              )}
            </>
          }
          showCredits={false}
          showNewChatButton={userRole === ROLE.CREATOR_SUB || userRole === ROLE.CREATOR_CONTENT ? true : false}
        />

        <main className="modern-contact-main">
          <div className="cm_dark_form msg_search">
            <span>
              <i className="fa-solid fa-magnifying-glass" />
            </span>
            <input
              type="text"
              placeholder="Search conversations..."
              className="form-control cm_form_control"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="msg_filter_bar">
            {messageFilterOptions.map((item) => {
              const isActive = item.title === activeTabTitle;
              return (
                <span
                  key={item.title}
                  role="button"
                  tabIndex={0}
                  className={isActive ? 'active' : ''}
                  onClick={() => setActiveTab(item.title)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setActiveTab(item.title);
                    }
                  }}
                >
                  {renderFilterTabIcon(item.title)}
                  {item.title} {item.count ? `(${item.count})` : ''}
                </span>
              );
            })}
          </div>

          {(userRole === ROLE.CREATOR_SUB || userRole === ROLE.CREATOR_CONTENT) &&
            (activeTabSlug === 'all' || activeTabSlug === 'archived') && (
              <div className="chat_are_lock">
                <i className="fa-solid fa-lock" />
                {userRole === ROLE.CREATOR_SUB
                  ? 'Chats are locked for subs until they re-subscribe'
                  : 'Chats are locked for subs until they re-follow you'}
              </div>
            )}

          {(userRole === ROLE.USER) &&
            (activeTabSlug === 'all' || activeTabSlug === 'archived') && (
              <div className="chat_are_lock">
                <i className="fa-solid fa-lock" />
                Chats are locked until you follow or subscribe again
              </div>
            )}

          <div className="msg_user_list" translate="no">
            {isLoading ? (
              <div
                className="d-flex flex-column align-items-center justify-content-center p-4 msg-loading-container"
                key={loadingState}
              >
                <Loader type="Oval" color="#F334BE" height={40} width={40} />
                <span className="loader-title mt-3 mb-0 msg-loader-title">
                  {getLoadingMessage(loadingState, matrixLoading)}
                </span>
              </div>
            ) : roomsForActiveTab.length === 0 ? (
              <div className="text-center p-4">No conversations yet</div>
            ) : (
              <>
                {visibleRoomsForActiveTab.map((item, index) => {
                  const roomKey = item?.roomId ?? item?._id;
                  const stableKey =
                    roomKey != null && String(roomKey).length > 0
                      ? String(roomKey)
                      : item?.userId != null
                        ? `user-${item.userId}`
                        : `room-${index}`;
                  return (
                    <MessageCard
                      key={stableKey}
                      item={item}
                      fromTab={activeTabSlug}
                      isCreator={isCreator}
                      refreshRooms={refreshRooms}
                    />
                  );
                })}
                {hasMoreToShow ? (
                  <div
                    ref={listLoadMoreSentinelRef}
                    className="msg_list_load_more_sentinel"
                    aria-hidden="true"
                  />
                ) : null}
                {roomsLoadingMore ? (
                  <div className="d-flex justify-content-center p-3">
                    <Loader type="Oval" color="#F334BE" height={24} width={24} />
                  </div>
                ) : null}
              </>
            )}
          </div>
        </main>
        <ModernFootermenu />
      </div>

      <MessagingExplainedModal
        userRole={userRole}
        isOpen={showMsgOverviewModal}
        toggle={handleMessagingExplainedClose}
      />
      <PurchaseCreditsModal
        isOpen={showPurchaseCreditsModal}
        onClose={() => setShowPurchaseCreditsModal(false)}
      />
    </>
  );
};

export default Messaging;